import { type KyInstance } from "ky";
import { DEFAULT_HEADER_GROUP_ID } from "@/configs/system";
import { estaDoisFatoresPendente } from "./estadoAutenticacao.space";
import type { ISpaceLoginResponse } from "./dtos/authenticator.dto";
import { spaceApiAuthenticator } from "./clienteSpace";
import {
	aplicarSessaoAutenticadaSpaceApi,
	aplicarTokenRotacionadoSpaceApi,
	atualizarTokenUmaVezSpaceApi,
	definirExecutorRefreshTokenSpaceApi,
	notificarFalhaAutenticacaoSpaceApi,
	getGrupoAtualAutenticacaoSpaceApi,
} from "./sessaoAutenticacao.space";

const ENDPOINTS_INVALIDAM_SESSAO = ["/login", "/refresh"] as const;

function deveInvalidarSessao(requisicao: Request): boolean {
	try {
		const path = new URL(requisicao.url).pathname;
		return ENDPOINTS_INVALIDAM_SESSAO.some((endpoint) => path.includes(endpoint));
	} catch {
		return ENDPOINTS_INVALIDAM_SESSAO.some((endpoint) => requisicao.url.includes(endpoint));
	}
}

async function realizarRefreshToken(): Promise<string | null> {
	if (estaDoisFatoresPendente()) return null;

	const groupId = getGrupoAtualAutenticacaoSpaceApi();
	if (!groupId) return null;

	try {
		const resposta = await spaceApiAuthenticator
			.post("refresh", {
				headers: { [DEFAULT_HEADER_GROUP_ID]: groupId },
			})
			.json<ISpaceLoginResponse>();

		const token = resposta?.token ?? null;
		if (token)
			aplicarSessaoAutenticadaSpaceApi({
				token,
				groupId,
			});

		return token;
	} catch {
		return null;
	}
}

definirExecutorRefreshTokenSpaceApi(realizarRefreshToken);

export const spaceApiAuth: KyInstance = spaceApiAuthenticator.extend({
	hooks: {
		afterResponse: [
			async (requisicao, opcoes, resposta) => {
				const tokenRotacionado = resposta.headers.get("New-Access-Token");
				if (tokenRotacionado && !estaDoisFatoresPendente()) aplicarTokenRotacionadoSpaceApi(tokenRotacionado);

				if (requisicao.url.includes("/refresh")) return resposta;
				if (resposta.status !== 401) return resposta;
				if (estaDoisFatoresPendente()) return resposta;
				if (!deveInvalidarSessao(requisicao)) return resposta;

				if (requisicao.headers.get("x-auth-retry") === "1") {
					notificarFalhaAutenticacaoSpaceApi();
					return resposta;
				}

				const novoToken = await atualizarTokenUmaVezSpaceApi();
				if (!novoToken) {
					notificarFalhaAutenticacaoSpaceApi();
					return resposta;
				}

				const cabecalhosRetry = new Headers(requisicao.headers);
				cabecalhosRetry.set("Authorization", `Bearer ${novoToken}`);
				cabecalhosRetry.set("x-auth-retry", "1");

				const requisicaoRetry = new Request(requisicao, {
					headers: cabecalhosRetry,
				});

				return spaceApiAuth(requisicaoRetry, opcoes);
			},
		],
	},
});

export {
	aplicarSessaoAutenticadaSpaceApi,
	atualizarTokenUmaVezSpaceApi,
	definirCabecalhosGlobaisAutenticacaoSpaceApi,
	definirCallbackFalhaAutenticacaoSpaceApi,
	limparSessaoAutenticadaSpaceApi,
	notificarFalhaAutenticacaoSpaceApi,
	getCabecalhosGlobaisAutenticacaoSpaceApi,
	getGrupoAtualAutenticacaoSpaceApi,
	getTokenJwtSpaceApi,
	removerCabecalhosGlobaisAutenticacaoSpaceApi,
} from "./sessaoAutenticacao.space";

export default spaceApiAuth;
